import React, { useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  TextField as MuiTextField,
  Grid,
  CardHeader,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";

import SaveCancel from "../../../components/SaveCancel";
import usePlan from "../../../hooks/usePlan";
import TabPanel from "../../../components/TabPanel";
import { a11yProps } from "../../../utils/common";
import PlanDetail from "./components/PlanDetail";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import RateDataGridPage from "./components/rate";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const initialValues = {
  planName: "",
  uploadSpeed: "",
  downloadSpeed: "",
  planType: "",
  planDataType: "",
  downloadLimit: "",
  uploadLimit: "",
  taxType: "INCLUDING_TAX",
  serviceType: "INTERNET",
  status: "ACTIVE",
  fallbackPlan: null,
  ottPlan: null,
  nasGroup: null,
};

const validationSchema = Yup.object().shape({
  planName: Yup.string()
    .required("Required")
    .matches(
      /^[a-zA-Z0-9_]*$/g,
      "Plan name should be alpha numberic and underscore, there is no space allowed",
    ),
  uploadSpeed: Yup.string().required("Required"),
  downloadSpeed: Yup.string().required("Required"),
  planType: Yup.string().required("Required"),
  planDataType: Yup.string().when("planType", {
    is: (val) => val === "Fallback", // When planType is "Fallback"
    then: () => Yup.string().oneOf(["Unlimited"], "Plan Data Type must be Unlimited").required(),
    otherwise: () => Yup.string().required("Plan Data Type is required"), // Required for other plan types
  }),
  // planDataType: Yup.string().required("Required"),
  // downloadLimit: Yup.string().when("planDataType", {
  //   is: (val) => (val && (val === "Limited" || val === "Unlimited_FUP") ? true : false),
  //   then: Yup.string().required("Download limit is required"),
  // }),
  // uploadLimit: Yup.string().when("planDataType", {
  //   is: (val) => (val && (val === "Limited" || val === "Unlimited_FUP") ? true : false),
  //   then: Yup.string().required("Upload limit is required"),
  // }),

  totalLimit: Yup.string().when("planDataType", {
    is: (val) => (val && (val === "Limited" || val === "Unlimited_FUP") ? true : false),
    then: () => Yup.string().required("Total limit is required"),
  }),
  serviceType: Yup.string().required("Required"),
  taxType: Yup.string().required("Required"),
  nasGroup: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Nas group required"),
  ottPlan: Yup.string().when("serviceType", {
    is: (val) => val === "INTERNET_OTT", // When planType is "Fallback"
    then: () =>
      Yup.object()
        .shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
        .nullable()
        .required("OTT Plan is required"),
    otherwise: () => Yup.mixed().nullable(),
  }),
  status: Yup.string().required("Required"),
});

function AddPlan() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { addPlan, hasError, errorMessage, isSubmitting, plan } = usePlan();
  const [value, setValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = methods;
  const navigate = useNavigate();

  useEffect(() => {
    if (Object.keys(plan).length != 0) {
      navigate("/radius/plan/edit", { state: { id: plan.planId } });
    }
  }, [plan]);

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Radius</Typography>
        <Typography>Plan</Typography>
        <Typography>Add</Typography>
      </Breadcrumbs>
    );
  };

  // const handleSubmit = async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
  //   try {
  //     console.log(values, "fff");
  //     await addPlan(values);
  //     resetForm();
  //     setStatus({ sent: true });
  //     setSubmitting(false);
  //   } catch (error) {
  //     setStatus({ sent: false });
  //     setErrors({ submit: error.message });
  //     setSubmitting(false);
  //   }
  // };

  const onSubmit = async (data) => {
    try {
      console.log(data.fallbackPlan, "data.fallbackPlan");
      data["nasGroup"] = {
        nasGroupId: data?.nasGroup?.value,
        nasGroupName: data?.nasGroup?.label,
      };
      if (data.fallbackPlan != null) {
        data["fallbackPlan"] = {
          planId: data?.fallbackPlan?.value,
          planName: data?.fallbackPlan?.label,
        };
      }
      if (data.ottPlan != null) {
        data["ottPlan"] = {
          ottPlanId: data?.ottPlan?.value,
          ottPlanName: data?.ottPlan?.label,
        };
      }

      await addPlan(data);
      //reset();
      dispatch(showSuccessSnackbar("Plan added successfully!"));
    } catch (error) {
      console.log(error);
      dispatch(showErrorSnackbar(error.message));
    }
  };

  const cancelBtn = () => {
    navigate("/radius/plan/list");
  };

  const serviceType = watch("serviceType");

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("add_plan")} subheader={breadCrump()} />
          <CardContent>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs value={value} onChange={handleTabChange} aria-label="Plan">
                    <Tab label="Plan Details" {...a11yProps(0)} />
                    <Tab label="Rate Details" {...a11yProps(1)} />
                  </Tabs>
                  <TabPanel value={value} index={0}>
                    <PlanDetail validationFields={validationSchema.fields} />
                  </TabPanel>
                  <TabPanel value={value} index={1}>
                    <RateDataGridPage planId={plan?.planId} serviceType={serviceType} />
                  </TabPanel>
                </Box>

                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default AddPlan;
